import { useLocation, useNavigate } from "react-router-dom"
import { Copy } from "lucide-react"
import toast from "react-hot-toast"
import { useAuthStore } from "@/store/authStore"
import { cn } from "@/lib/utils"
import { convencion, estiloBandera } from "@/lib/pais"
import { navFor, todosLosItems, activoEn } from "../nav/navConfig"
import ThemePicker from "../ThemePicker"

interface SidebarProps {
  onNavigate?: () => void
}

/**
 * Menú lateral de escritorio. Los grupos salen de `navConfig` (la misma fuente que la barra inferior
 * y el buscador). Asesor cd-2b: Trabajo · Mi país (gerente) · Dinero · Aprende; la cuenta vive en el
 * menú del avatar.
 */
export default function Sidebar({ onNavigate }: SidebarProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { partner } = useAuthStore()

  const def = navFor(partner)
  const isActive = activoEn(location.pathname, todosLosItems(def).map((i) => i.path))
  const esCd2b = partner?.organizationType === "cd2b_consultant"
  const pais = convencion(partner?.country)
  const referralCode = partner?.referralCode || "XXXXXX"

  const ir = (path: string) => {
    navigate(path)
    onNavigate?.()
  }

  const copiarCodigo = async () => {
    try {
      await navigator.clipboard.writeText(referralCode)
      toast.success("Código copiado")
    } catch {
      toast.error("No se pudo copiar")
    }
  }

  return (
    <div className="flex h-full flex-col bg-card">
      <div className="flex items-center gap-2.5 px-5 pt-5 pb-4">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-white font-display font-semibold">
          {esCd2b ? "2b" : "CD"}
        </div>
        <div className="min-w-0">
          <p className="text-body font-semibold leading-tight text-foreground">{esCd2b ? "cd-2b" : "CoreDesk"}</p>
          <p className="text-caption text-subtle">{esCd2b ? "Portal del asesor" : "Portal de socios"}</p>
        </div>
      </div>

      {esCd2b ? (
        <div className="mx-4 mb-2 flex items-center gap-3 rounded-md bg-surface-muted px-3 py-2.5">
          <span aria-hidden="true" className="h-3.5 w-5 shrink-0 rounded-[2px] ring-1 ring-line" style={estiloBandera(partner?.country)} />
          <div className="min-w-0">
            <p className="text-body-sm font-semibold text-foreground truncate">{pais.nombre || "Sin país asignado"}</p>
            <p className="text-caption text-subtle">
              {partner?.rolCd2b === "gerente" ? "Gerente de país" : partner?.vinculo === "empleado" ? "Asesor empleado" : "Asesor independiente"}
            </p>
          </div>
        </div>
      ) : (
        <div className="mx-4 mb-2 rounded-md border border-primary-100 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/40 p-3">
          <p className="text-caption text-primary-700 dark:text-primary-300 mb-1.5">Tu código de referido</p>
          <div className="flex items-center justify-between gap-2">
            <code className="text-body font-mono font-semibold text-primary-900 dark:text-primary-100 tracking-wide">{referralCode}</code>
            <button
              type="button"
              onClick={copiarCodigo}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors"
              aria-label="Copiar código"
            >
              <Copy size={15} />
            </button>
          </div>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto px-3 pb-4" aria-label="Menú principal">
        {def.groups.map((g) => (
          <div key={g.key} className="mt-4">
            <p className="px-3 mb-1.5 text-micro uppercase tracking-wider text-subtle">
              {g.key === "pais" && pais.nombre ? `Mi país · ${pais.nombre}` : g.label}
            </p>
            <ul className="space-y-0.5">
              {g.items.map((item) => {
                const active = isActive(item.path)
                return (
                  <li key={item.path}>
                    <button
                      type="button"
                      onClick={() => ir(item.path)}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-md px-3 py-2 text-body-sm font-medium transition-colors min-h-[40px]",
                        active
                          ? "bg-primary-50 text-primary-700 dark:bg-primary/20 dark:text-primary-100 font-semibold"
                          : item.highlight
                            ? "text-accent-800 dark:text-accent-200 bg-accent-50/60 dark:bg-accent-900/20 hover:bg-accent-100 dark:hover:bg-accent-900/40"
                            : "text-neutral-700 dark:text-neutral-300 hover:bg-surface-muted hover:text-foreground"
                      )}
                    >
                      <span className={cn("shrink-0", active ? "text-primary dark:text-primary-300" : "text-subtle")}>{item.icon}</span>
                      <span className="truncate">{item.text}</span>
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="border-t border-line px-4 py-3 space-y-3">
        <div className="flex items-center gap-2 text-caption text-subtle">
          <span className={cn("h-2 w-2 rounded-full", partner?.status === "active" ? "bg-success" : "bg-warning")} />
          {partner?.status === "active" ? "Cuenta activa" : "Cuenta pendiente de aprobación"}
        </div>
        <ThemePicker compacto />
      </div>
    </div>
  )
}
